import { Result } from "../../../../api/types";
import type { AggregatedQto } from "../../types";
import { UnauthorizedError, handleError } from "../../../../errors";
import { getValidAtoAccessToken, peekAtoAccessToken, refreshAtoAccessToken } from "./auth";
import { mapTakeoffItemsToAggregated } from "./mapTakeoffItems";
import type {
  AtoAccProject,
  AtoHub,
  AtoPackage,
  AtoQuantityDefinition,
  AtoTakeoffItemRaw,
  AtoTakeoffTypesPaginatedResponse,
  AtoTypeInfo,
} from "./types";

const APS_DATA_BASE = "https://developer.api.autodesk.com";
const ACC_TAKEOFF_BASE = "https://acc.autodesk.com/takeoff";

/** Deep link to a package in Autodesk Takeoff. Project ids carry a `b.` prefix the web app omits. */
export const getAtoPackageUrl = (projectId: string): string =>
  `${ACC_TAKEOFF_BASE}/packages/projects/${projectId.replace(/^b\./, "")}`;

/** Data Management API returns project IDs as "b.{guid}"; Construction/Takeoff APIs expect just the GUID. */
function stripProjectPrefix(projectId: string): string {
  return projectId.replace(/^b\./, "");
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v != null && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

/** Normalize Project / Takeoff list payloads (JSON:API `data`, plain arrays, or `results`). */
export function extractCollection(json: unknown): unknown[] {
  if (Array.isArray(json)) return json;
  const root = asRecord(json);
  if (!root) return [];
  if (Array.isArray(root.data)) return root.data;
  if (Array.isArray(root.results)) return root.results;
  const data = asRecord(root.data);
  if (data && Array.isArray(data.results)) return data.results;
  return [];
}

/** This helper function facilitates the fetching all pages fo results.
 * JSON:API pagination: `links.next.href` holds an absolute URL, and is absent on the last page.
 */
function jsonApiNextHref(json: unknown): string | null {
  const links = asRecord(asRecord(json)?.links);
  const next = asRecord(links?.next);
  const href = next && typeof next.href === "string" ? next.href : "";
  return href || null;
}

function jsonApiIdName(resource: unknown): { id: string; name: string } | null {
  const record = asRecord(resource);
  if (!record) return null;
  const id = typeof record.id === "string" ? record.id : "";
  if (!id) return null;
  const attrs = asRecord(record.attributes);
  const name =
    (attrs && typeof attrs.name === "string" && attrs.name.trim() && attrs.name) ||
    (attrs && typeof attrs.displayName === "string" && attrs.displayName.trim() && attrs.displayName) ||
    (typeof record.name === "string" && record.name.trim() && record.name) ||
    id;
  return { id, name };
}

async function atoFetchRaw(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await getValidAtoAccessToken();
  if (!token) {
    throw new UnauthorizedError("Not signed in to Autodesk");
  }
  const url = path.startsWith("http") ? path : `${APS_DATA_BASE}${path}`;
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string>),
  };
  if (!headers.Authorization) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (!headers.Accept) {
    headers.Accept = "application/json";
  }
  return fetch(url, { ...init, headers });
}

/**
 * Fetch ACC / APS APIs with Bearer token; on 401 refresh once and retry.
 */
export async function atoFetchJson<T>(path: string, init: RequestInit = {}): Promise<Result<T>> {
  try {
    let res = await atoFetchRaw(path, init);
    if (res.status === 401 && peekAtoAccessToken()) {
      const ok = await refreshAtoAccessToken();
      if (ok) {
        res = await atoFetchRaw(path, init);
      }
    }
    if (!res.ok) {
      let apiMessage = "API request failed";
      try {
        const body = (await res.json()) as { message?: string; detail?: string; errors?: { detail?: string }[] };
        if (body.detail) apiMessage = body.detail;
        else if (body.message) apiMessage = body.message;
        else if (body.errors?.[0]?.detail) apiMessage = body.errors[0].detail as string;
      } catch {
        // ignore
      }
      const rawErr = Object.assign(new Error(apiMessage), { statusCode: res.status });
      return { success: false, error: handleError(rawErr, { context: "atoFetchJson", path, status: res.status }) };
    }
    const data = (await res.json()) as T;
    return { success: true, data };
  } catch (error) {
    return { success: false, error: handleError(error, { context: "atoFetchJson", path }) };
  }
}

export async function fetchAtoHubs(): Promise<Result<AtoHub[]>> {
  const result = await atoFetchJson<unknown>("/project/v1/hubs");
  if (!result.success) return result;
  const rows: AtoHub[] = [];
  for (const item of extractCollection(result.data)) {
    const pair = jsonApiIdName(item);
    if (pair) rows.push({ id: pair.id, name: pair.name });
  }
  return { success: true, data: rows };
}

/** Data Management api returns 200 projects for each page fetched. This max bound is a
 *  fail safe that stops an infinite loop. This limit means 10,000 projects max can be
 * loaded, which is beyond any practical bound.
 */
const MAX_PROJECT_PAGES = 50;

/**
 * List the projects the signed-in user is a member of within a hub.
 */
export async function fetchAtoProjects(hubId: string): Promise<Result<AtoAccProject[]>> {
  const rows: AtoAccProject[] = [];
  let path: string | null = `/project/v1/hubs/${encodeURIComponent(hubId)}/projects?page[limit]=200`;

  for (let page = 0; path && page < MAX_PROJECT_PAGES; page++) {
    const result: Result<unknown> = await atoFetchJson<unknown>(path);
    if (!result.success) return result;
    for (const item of extractCollection(result.data)) {
      const pair = jsonApiIdName(item);
      if (pair) rows.push({ id: pair.id, name: pair.name });
    }
    path = jsonApiNextHref(result.data);
  }

  return { success: true, data: rows };
}

export async function fetchAtoPackages(projectId: string): Promise<Result<AtoPackage[]>> {
  const strippedId = stripProjectPrefix(projectId);
  const url = `/construction/takeoff/v1/projects/${encodeURIComponent(strippedId)}/packages`;
  const result = await atoFetchJson<unknown>(url);
  if (!result.success) return result;
  const collection = extractCollection(result.data);
  const rows: AtoPackage[] = [];
  for (const item of collection) {
    const pair = jsonApiIdName(item);
    if (pair) rows.push({ id: pair.id, name: pair.name });
    else {
      const record = asRecord(item);
      const id = typeof record?.id === "string" ? record.id : "";
      if (id) rows.push({ id, name: id });
    }
  }
  return { success: true, data: rows };
}

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/**
 * Extract an Ediphi product UUID from a quantity definition's `outputName`.
 * The output name embeds the product ID as the last `|`-delimited segment, e.g.
 * `92513.330 | Ceramic Tile - Floor | 2d83dee1-84bc-4bfe-bac1-3eb9a2677c..`.
 */
function extractEdiphiProductId(quantityDef: AtoQuantityDefinition | null | undefined): string | null {
  if (!quantityDef?.outputName) return null;
  const match = UUID_RE.exec(quantityDef.outputName);
  return match ? match[0] : null;
}

export async function fetchAtoTakeoffTypes(
  projectId: string,
  packageId: string
): Promise<Result<Map<string, AtoTypeInfo>>> {
  const guid = stripProjectPrefix(projectId);
  const limit = 100;
  let offset = 0;
  const byId = new Map<string, AtoTypeInfo>();

  for (;;) {
    const q = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    const result = await atoFetchJson<AtoTakeoffTypesPaginatedResponse>(
      `/construction/takeoff/v1/projects/${encodeURIComponent(guid)}/packages/${encodeURIComponent(packageId)}/takeoff-types?${q}`
    );
    if (!result.success) return result;
    const batch = result.data?.results ?? [];
    for (const type of batch) {
      if (!type.id) continue;
      const pqd = type.primaryQuantityDefinition ?? null;
      const ediphiProductId = extractEdiphiProductId(pqd);
      const unitOfMeasure = pqd?.unitOfMeasure?.trim() || null;
      byId.set(type.id, { name: type.name, unitOfMeasure, ediphiProductId });
    }
    if (batch.length < limit) break;
    offset += limit;
  }

  return { success: true, data: byId };
}

/**
 * Validate a merged record satisfies the `AtoTakeoffItemRaw` contract.
 * Per ACC's API, every takeoff item must have a string `id` and `takeoffTypeId`;
 * rows missing either are dropped here so downstream code can trust the type.
 */
function toTakeoffItemRaw(merged: Record<string, unknown>): AtoTakeoffItemRaw | null {
  const id = typeof merged.id === "string" ? merged.id : "";
  const takeoffTypeId = typeof merged.takeoffTypeId === "string" ? merged.takeoffTypeId : "";
  if (!id || !takeoffTypeId) return null;
  return merged as unknown as AtoTakeoffItemRaw;
}

function extractTakeoffItemsPayload(json: unknown): AtoTakeoffItemRaw[] {
  const list = extractCollection(json);
  const items: AtoTakeoffItemRaw[] = [];
  for (const item of list) {
    const record = asRecord(item);
    if (!record) continue;
    const attrs = asRecord(record.attributes);
    const merged: Record<string, unknown> = attrs ? { ...attrs, ...record } : { ...record };
    if (typeof merged.id !== "string" || !merged.id) {
      const aid = attrs && typeof attrs.id === "string" ? attrs.id : "";
      if (aid) merged.id = aid;
    }
    const validated = toTakeoffItemRaw(merged);
    if (validated) items.push(validated);
  }
  return items;
}

export async function fetchAllAtoTakeoffItems(
  projectId: string,
  packageId: string
): Promise<Result<AtoTakeoffItemRaw[]>> {
  const limit = 100;
  let offset = 0;
  const all: AtoTakeoffItemRaw[] = [];

  for (;;) {
    const q = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    const path = `/construction/takeoff/v1/projects/${encodeURIComponent(stripProjectPrefix(projectId))}/packages/${encodeURIComponent(packageId)}/takeoff-items?${q}`;
    const result = await atoFetchJson<unknown>(path);
    if (!result.success) return result;
    const batch = extractTakeoffItemsPayload(result.data);
    if (batch.length === 0) break;
    all.push(...batch);
    if (batch.length < limit) break;
    offset += limit;
  }

  return { success: true, data: all };
}

export async function loadAtoAggregatedForPackage(
  projectId: string,
  packageId: string
): Promise<Result<{ aggregated: AggregatedQto[] }>> {
  const typesResult = await fetchAtoTakeoffTypes(projectId, packageId);
  const typeById = typesResult.success && typesResult.data ? typesResult.data : new Map<string, AtoTypeInfo>();

  const itemsResult = await fetchAllAtoTakeoffItems(projectId, packageId);
  if (!itemsResult.success) return itemsResult;

  const items = itemsResult.data ?? [];
  const aggregated = mapTakeoffItemsToAggregated(items, typeById);
  return {
    success: true,
    data: { aggregated },
  };
}
